import { useState } from "react";
import {
  Footprints,
  Bath,
  ChefHat,
  Flame,
  Gem,
  Building2,
  ArrowUpRight,
  Plus,
  X,
  ImageIcon,
  Check,
} from "lucide-react";
import commercialFloorPatternAsset from "@/assets/commercial-floor-pattern.jpg.asset.json";
import flooring from "@/assets/gallery/300.webp";
import bathroom from "@/assets/gallery/140.webp";
import bathroomMobile from "@/assets/home/140-card.webp";
import backsplash from "@/assets/gallery/074.webp";
import backsplashMobile from "@/assets/home/074-card.avif";
import fireplace from "@/assets/gallery/260.webp";
import mosaic from "@/assets/gallery/205.webp";
import mosaicMobile from "@/assets/home/205-card.avif";

type Service = {
  icon: typeof Footprints;
  title: string;
  short: string;
  long: string;
  bullets: string[];
  image?: string; // swap in real image URL when ready
  imageMobile?: string;
  imageMobileType?: string;
  imageAlt?: string;
  featured?: boolean;
};

const services: Service[] = [
  {
    icon: Footprints,
    title: "Custom Stone & Tile Flooring",
    short: "Our specialty. Natural stone, porcelain, and intricate inlays up to 4' x 4'.",
    long: "Flooring is what we're known for. From wide-format porcelain to hand-cut natural stone, every floor we install is built on substrates prepared to last decades — not just to pass an inspection.",
    bullets: [
      "Tile sizes up to 4' x 4'",
      "Natural stone, porcelain & ceramic",
      "Custom inlays and pattern work",
      "Substrate prep built for longevity",
    ],
    featured: true,
    image: flooring,
    imageAlt: "Custom diamond-pattern tile flooring installation",
  },
  {
    icon: Bath,
    title: "Bathrooms & Showers",
    short: "Waterproof systems, curbless showers, and custom niches done right.",
    long: "We build bathrooms the way they should be built — fully waterproofed, properly sloped, and finished with the small details that separate a custom bath from a builder-grade one. No shortcuts behind the tile.",
    bullets: [
      "Schluter / fully waterproofed systems",
      "Curbless and linear-drain showers",
      "Custom niches, benches & seats",
      "Steam shower & wet room builds",
    ],
    image: bathroom,
    imageMobile: bathroomMobile,
    imageAlt: "Custom freestanding tub bathroom installation",
  },
  {
    icon: ChefHat,
    title: "Kitchens & Backsplashes",
    short: "Statement backsplashes designed to live alongside cabinetry, lighting, and stone.",
    long: "A backsplash is the one piece of the kitchen you see every day. We help dial in layout, grout, and finish so it complements your countertops and cabinetry instead of fighting them.",
    bullets: [
      "Full-height and feature-wall backsplashes",
      "Handmade and zellige tile",
      "Precision cuts around outlets & hoods",
      "Color-matched grout & sealing",
    ],
    image: backsplash,
    imageMobile: backsplashMobile,
    imageMobileType: "image/avif",
    imageAlt: "Custom patterned tile kitchen backsplash with pot filler",
  },
  {
    icon: Flame,
    title: "Fireplaces & Surrounds",
    short: "Floor-to-ceiling stone and bold focal points built to last generations.",
    long: "Fireplaces are statement pieces. We work in full-bed mortar, ledgestone, slab, and large-format tile to create surrounds that anchor a room without ever feeling busy.",
    bullets: [
      "Floor-to-ceiling stone walls",
      "Full-bed mortar setting",
      "Hearths, mantels & custom returns",
      "Indoor & outdoor installations",
    ],
    image: fireplace,
    imageAlt: "Custom stone fireplace and built-in surround",
  },
  {
    icon: Gem,
    title: "Natural Stone & Mosaics",
    short: "Marble, travertine, slate, and intricate mosaic layouts.",
    long: "Natural stone rewards patience. We dry-lay, blend, and book-match every piece so veining and color move the way they should — and we execute mosaic patterns at masterpiece-level detail.",
    bullets: [
      "Marble, travertine, slate & limestone",
      "Book-matched and vein-matched layouts",
      "Hand-set mosaic patterns",
      "Honing, sealing & long-term care guidance",
    ],
    image: mosaic,
    imageMobile: mosaicMobile,
    imageMobileType: "image/avif",
    imageAlt: "Natural stone pebble mosaic floor installation",
  },
  {
    icon: Building2,
    title: "Commercial Projects",
    short: "Restaurants, retail, lobbies, and offices — on the schedules builders depend on.",
    long: "We've been working alongside Utah builders and GCs for 30 years. Commercial work means hitting the schedule without compromising the quality the project was designed to have.",
    bullets: [
      "Restaurants, retail & hospitality",
      "Corporate lobbies & offices",
      "Coordination with GCs and trades",
      "Punch-list and warranty support",
    ],
    image: commercialFloorPatternAsset.url,
    imageAlt: "Commercial floor tile pattern installation",
  },
];

export function ServicesGrid() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="services" className="py-20 md:py-28 bg-background">
      <div className="container-prose">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="eyebrow">Our services</span>
            <h2 className="mt-3 font-display text-3xl md:text-5xl text-foreground leading-tight">
              From a single backsplash to a full custom build.
            </h2>
          </div>
          <p className="md:max-w-sm text-muted-foreground">
            Click any service to see what's included. Residential and commercial tile work across
            Park City, Salt Lake, Davis, Summit, and Utah counties.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <ServiceCard
              key={s.title}
              service={s}
              isOpen={openIndex === i}
              onToggle={() => setOpenIndex(openIndex === i ? null : i)}
            />
          ))}
        </div>
      </div>

      {openIndex !== null && (
        <ServiceModal service={services[openIndex]} onClose={() => setOpenIndex(null)} />
      )}
    </section>
  );
}

function ServiceCard({
  service,
  isOpen,
  onToggle,
}: {
  service: Service;
  isOpen: boolean;
  onToggle: () => void;
}) {
  const Icon = service.icon;
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpen}
      className={`group relative text-left bg-card border border-border overflow-hidden transition-all duration-300 hover:border-primary hover:-translate-y-1 hover:shadow-[var(--shadow-card)] focus:outline-none focus-visible:border-primary ${
        service.featured ? "ring-1 ring-accent/40" : ""
      }`}
    >
      {/* Image / placeholder */}
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        {service.image ? (
          <picture>
            {service.imageMobile ? (
              <source
                media="(max-width: 767px)"
                srcSet={service.imageMobile}
                type={service.imageMobileType ?? "image/webp"}
              />
            ) : null}
            <img
              src={service.image}
              alt={service.imageAlt ?? service.title}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
          </picture>
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-muted to-secondary text-muted-foreground">
            <ImageIcon className="h-8 w-8" strokeWidth={1.4} />
            <span className="text-[10px] uppercase tracking-[0.22em]">Photo coming soon</span>
          </div>
        )}
        {service.featured && (
          <div className="absolute top-3 left-3 bg-accent text-accent-foreground text-[10px] uppercase tracking-[0.22em] font-semibold px-2.5 py-1">
            ★ Specialty
          </div>
        )}
        <div className="absolute bottom-3 right-3 h-9 w-9 bg-background/95 border border-border flex items-center justify-center text-primary transition-transform duration-300 group-hover:rotate-90">
          <Plus className="h-4 w-4" strokeWidth={2} />
        </div>
      </div>

      <div className="p-6">
        <Icon className="h-7 w-7 text-primary mb-4" strokeWidth={1.4} />
        <h3 className="font-display text-xl text-foreground">{service.title}</h3>
        <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{service.short}</p>
        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-primary">
          View details
          {![
            "Bathrooms & Showers",
            "Kitchens & Backsplashes",
            "Fireplaces & Surrounds",
            "Natural Stone & Mosaics",
            "Commercial Projects",
          ].includes(service.title) && <ArrowUpRight className="h-3.5 w-3.5" />}
        </span>
      </div>
    </button>
  );
}

function ServiceModal({ service, onClose }: { service: Service; onClose: () => void }) {
  const Icon = service.icon;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={service.title}
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-6 animate-fade-in"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-ink/70 backdrop-blur-sm" />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-background w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-[var(--shadow-elegant)] animate-scale-in"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 h-10 w-10 bg-background/95 border border-border flex items-center justify-center text-foreground hover:bg-primary hover:text-primary-foreground hover:border-primary transition-colors"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative aspect-[16/9] w-full bg-muted overflow-hidden">
          {service.image ? (
            <picture>
              {service.imageMobile ? (
                <source
                  media="(max-width: 767px)"
                  srcSet={service.imageMobile}
                  type={service.imageMobileType ?? "image/webp"}
                />
              ) : null}
              <img
                src={service.image}
                alt={service.imageAlt ?? service.title}
                className="h-full w-full object-cover"
              />
            </picture>
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-gradient-to-br from-muted to-secondary text-muted-foreground">
              <ImageIcon className="h-10 w-10" strokeWidth={1.4} />
              <span className="text-xs uppercase tracking-[0.22em]">Photo coming soon</span>
            </div>
          )}
        </div>

        <div className="p-7 md:p-10">
          <div className="flex items-center gap-3">
            <Icon className="h-6 w-6 text-primary" strokeWidth={1.4} />
            {service.featured && (
              <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-accent">
                ★ Featured Specialty
              </span>
            )}
          </div>
          <h3 className="mt-3 font-display text-3xl md:text-4xl text-foreground leading-tight">
            {service.title}
          </h3>
          <p className="mt-4 text-muted-foreground leading-relaxed">{service.long}</p>

          <div className="mt-7 border-t border-border pt-6">
            <div className="text-xs uppercase tracking-[0.22em] font-semibold text-foreground mb-4">
              What's included
            </div>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-3">
              {service.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-foreground">
                  <span className="mt-0.5 h-5 w-5 flex-shrink-0 bg-primary/10 text-primary flex items-center justify-center">
                    <Check className="h-3 w-3" strokeWidth={2.5} />
                  </span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <a
              href="/#contact"
              onClick={onClose}
              className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-primary/90 transition-colors"
            >
              Get a Quote <ArrowUpRight className="h-4 w-4" />
            </a>
            <a
              href="tel:8015982226"
              className="inline-flex items-center justify-center gap-2 border border-border px-7 py-4 text-sm font-semibold uppercase tracking-wider text-foreground hover:border-primary hover:text-primary transition-colors"
            >
              Call (801) 598-2226
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
